import { useEffect, useRef, useState } from "react";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import Draggable from "react-draggable";
import { api2 } from "../utils/Url";

type Message = {
    role: "user" | "assistant";
    content: string;
};

const CHAT_WIDTH = 350;
const CHAT_HEIGHT = 500;
const MARGIN = 12;

export default function ChatBot() {

    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);

    const [messages, setMessages] = useState<Message[]>([
        {
            role: "assistant",
            content: "Hi! How can I help you today?"
        }
    ]);

    // Chat popup position
    const [chatPos, setChatPos] = useState({
        top: 100,
        left: 20
    });

    // Draggable button ref
    const nodeRef = useRef<HTMLButtonElement>(null);

    // Chat popup ref
    const chatRef = useRef<HTMLDivElement>(null);

    // Auto scroll ref
    const bottomRef = useRef<HTMLDivElement>(null);

    // Drag detection
    const dragStart = useRef({
        x: 0,
        y: 0
    });

    const wasDragged = useRef(false);

    //-----------------------------------------
    // Auto Scroll
    //-----------------------------------------

    useEffect(() => {

        bottomRef.current?.scrollIntoView({
            behavior: "smooth"
        });

    }, [messages, loading]);

    //-----------------------------------------
    // Outside Click
    //-----------------------------------------

    useEffect(() => {

        if (!open) return;

        const handleOutside = (e: MouseEvent) => {

            const target = e.target as Node;

            if (
                !chatRef.current?.contains(target) &&
                !nodeRef.current?.contains(target)
            ) {
                setOpen(false);
            }

        };

        document.addEventListener("mousedown", handleOutside);

        return () => {
            document.removeEventListener("mousedown", handleOutside);
        };

    }, [open]);

    //-----------------------------------------
    // Calculate Popup Position
    //-----------------------------------------

    const computeChatPosition = () => {

        if (!nodeRef.current) return;

        const rect = nodeRef.current.getBoundingClientRect();

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let top = rect.top - CHAT_HEIGHT - MARGIN;

        if (top < MARGIN) {

            top = rect.bottom + MARGIN;

        }

        if (top + CHAT_HEIGHT > viewportHeight - MARGIN) {

            top = viewportHeight - CHAT_HEIGHT - MARGIN;

        }

        let left = rect.left;

        if (left + CHAT_WIDTH > viewportWidth - MARGIN) {

            left = viewportWidth - CHAT_WIDTH - MARGIN;

        }

        if (left < MARGIN) {

            left = MARGIN;

        }

        setChatPos({
            top,
            left
        });

    };

    //-----------------------------------------
    // Drag
    //-----------------------------------------

    const handleDragStart = (_e: any, data: any) => {

        wasDragged.current = false;

        dragStart.current = {
            x: data.x,
            y: data.y
        };

    };

    const handleDrag = (_e: any, data: any) => {

        const dx = Math.abs(data.x - dragStart.current.x);

        const dy = Math.abs(data.y - dragStart.current.y);

        if (dx > 4 || dy > 4) {

            wasDragged.current = true;

        }

    };

    //-----------------------------------------
    // Open Close
    //-----------------------------------------

    const handleButtonClick = () => {

        if (wasDragged.current) {

            wasDragged.current = false;

            return;

        }

        if (!open) {

            computeChatPosition();

        }

        setOpen(prev => !prev);

    };

    //-----------------------------------------
    // Send Message
    //-----------------------------------------

    const sendMessage = async () => {

        if (!input.trim() || loading)
            return;

        const question = input.trim();

        setMessages(prev => [

            ...prev,

            {
                role: "user",
                content: question
            }

        ]);

        setInput("");

        setLoading(true);

        try {

            const response = await api2.post("/ask", {

                question

            });

            setMessages(prev => [

                ...prev,

                {
                    role: "assistant",
                    content: response.data?.answer ?? "No response."
                }

            ]);

        }
        catch (err) {

            console.error(err);

            setMessages(prev => [

                ...prev,

                {
                    role: "assistant",
                    content: "Something went wrong."
                }

            ]);

        }
        finally {

            setLoading(false);

        };

    };
        return (
        <>
            {open && (
                <div
                    ref={chatRef}
                    style={{
                        position: "fixed",
                        top: chatPos.top,
                        left: chatPos.left,
                        width: CHAT_WIDTH,
                        height: CHAT_HEIGHT,
                        background: "#fff",
                        borderRadius: 16,
                        boxShadow: "0 10px 30px rgba(0,0,0,.2)",
                        display: "flex",
                        flexDirection: "column",
                        zIndex: 9999,
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            padding: "15px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            background: "#395071",
                            color: "#fff",
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            fontWeight: "bold",
                        }}
                    >
                        <span>ChatBot</span>

                        <button
                            onClick={() => setOpen(false)}
                            style={{
                                background: "transparent",
                                border: "none",
                                color: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Messages */}
                    <div
                        style={{
                            flex: 1,
                            overflowY: "auto",
                            padding: 10,
                        }}
                    >
                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                style={{
                                    display: "flex",
                                    justifyContent:
                                        msg.role === "user"
                                            ? "flex-end"
                                            : "flex-start",
                                    marginBottom: 10,
                                }}
                            >
                                <div
                                    style={{
                                        maxWidth: "75%",
                                        padding: "10px 12px",
                                        borderRadius: 12,
                                        background:
                                            msg.role === "user"
                                                ? "#395071"
                                                : "#f1f5f9",
                                        color:
                                            msg.role === "user"
                                                ? "#fff"
                                                : "#000",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {msg.content}
                                </div>
                            </div>
                        ))}

                        {loading && (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    marginBottom: 10,
                                }}
                            >
                                <div
                                    style={{
                                        background: "#f1f5f9",
                                        padding: "10px 12px",
                                        borderRadius: 12,
                                    }}
                                >
                                    Typing...
                                </div>
                            </div>
                        )}

                        <div ref={bottomRef} />
                    </div>

                    {/* Footer */}
                    <div
                        style={{
                            display: "flex",
                            padding: 10,
                            borderTop: "1px solid #eee",
                        }}
                    >
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    sendMessage();
                                }
                            }}
                            placeholder="Type message..."
                            style={{
                                flex: 1,
                                padding: 10,
                                border: "1px solid #ddd",
                                borderRadius: 8,
                                outline: "none",
                            }}
                        />

                        <button
                            onClick={sendMessage}
                            disabled={loading || !input.trim()}
                            style={{
                                marginLeft: 8,
                                border: "none",
                                background: "#395071",
                                color: "#fff",
                                borderRadius: 8,
                                padding: "10px 14px",
                                cursor: "pointer",
                                opacity:
                                    loading || !input.trim() ? 0.6 : 1,
                            }}
                        >
                            <SendIcon />
                        </button>
                    </div>
                </div>
            )}

            <Draggable
                nodeRef={nodeRef}
                onStart={handleDragStart}
                onDrag={handleDrag}
            >
                <button
                    ref={nodeRef}
                    onClick={handleButtonClick}
                    style={{
                        position: "fixed",
                        bottom: 20,
                        right: 20,
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        border: "none",
                        background: "#395071",
                        color: "#fff",
                        cursor: "pointer",
                        boxShadow: "0 5px 20px rgba(0,0,0,.25)",
                        zIndex: 10000,
                        display: open ? "none" : "block",
                    }}
                >
                    <ChatIcon />
                </button>
            </Draggable>
        </>
    );
}